import { useEffect, useRef, useState } from 'react';
import { Button, Input, Textarea } from './ui';

// Vlastní dialogy místo syrových window.prompt/confirm — konzistentní vzhled,
// dark mode, mobil, autofocus, Enter/Escape. Promise API drží volání na jednom řádku:
//   const days = await askNumber({ title: 'Posunout termíny', label: 'O kolik dní…' });

interface BaseReq {
  title: string;
  body?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}
interface ConfirmReq extends BaseReq { kind: 'confirm'; resolve: (ok: boolean) => void }
interface TextReq extends BaseReq {
  kind: 'text';
  label?: string;
  placeholder?: string;
  initial?: string;
  multiline?: boolean;
  required?: boolean;
  resolve: (value: string | null) => void;
}
interface NumberReq extends BaseReq {
  kind: 'number';
  label?: string;
  placeholder?: string;
  initial?: string;
  hint?: string;
  resolve: (value: number | null) => void;
}
type Req = ConfirmReq | TextReq | NumberReq;

let push: ((r: Req) => void) | null = null;

export const askConfirm = (opts: Omit<ConfirmReq, 'kind' | 'resolve'>): Promise<boolean> =>
  new Promise((resolve) => {
    if (!push) { resolve(window.confirm(opts.body ? `${opts.title}\n\n${opts.body}` : opts.title)); return; }
    push({ kind: 'confirm', resolve, ...opts });
  });

export const askText = (opts: Omit<TextReq, 'kind' | 'resolve'>): Promise<string | null> =>
  new Promise((resolve) => {
    if (!push) { resolve(window.prompt(opts.title, opts.initial ?? '')); return; }
    push({ kind: 'text', resolve, ...opts });
  });

export const askNumber = (opts: Omit<NumberReq, 'kind' | 'resolve'>): Promise<number | null> =>
  new Promise((resolve) => {
    if (!push) {
      const v = window.prompt(opts.title, opts.initial ?? '');
      resolve(v === null ? null : Math.trunc(Number(v.replace('+', '').replace(',', '.'))) || null);
      return;
    }
    push({ kind: 'number', resolve, ...opts });
  });

// Host — jeden na aplikaci (mountnutý v Layoutu i na veřejných stránkách dle potřeby).
export function DialogHost() {
  const [req, setReq] = useState<Req | null>(null);
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const areaRef = useRef<HTMLTextAreaElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    push = (r) => { setValue(('initial' in r && r.initial) || ''); setReq(r); };
    return () => { push = null; };
  }, []);

  useEffect(() => {
    if (!req) return;
    // Potvrzení nemá pole: fokus jde na okno, jinak by nefungoval Esc. Po zavření se vrací zpět.
    const previous = document.activeElement as HTMLElement | null;
    const t = setTimeout(() => (req.kind === 'text' && (req as TextReq).multiline ? areaRef.current : inputRef.current ?? boxRef.current)?.focus(), 30);
    return () => { clearTimeout(t); previous?.focus?.({ preventScroll: true }); };
  }, [req]);

  if (!req) return null;

  const close = () => setReq(null);
  const cancel = () => {
    if (req.kind === 'confirm') req.resolve(false);
    else req.resolve(null);
    close();
  };
  const submit = () => {
    if (req.kind === 'confirm') { req.resolve(true); close(); return; }
    if (req.kind === 'text') {
      const v = value.trim();
      if ((req as TextReq).required && !v) return;
      req.resolve(v);
      close();
      return;
    }
    const n = Math.trunc(Number(value.replace('+', '').replace(',', '.')));
    if (!Number.isFinite(n)) return;
    req.resolve(n);
    close();
  };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { e.stopPropagation(); cancel(); }
    // Nebezpečnou akci Enter nepotvrdí, stačilo by omylem ťuknout dvakrát.
    if (e.key === 'Enter' && !req.danger && !(req.kind === 'text' && (req as TextReq).multiline)) { e.preventDefault(); submit(); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-black/50 backdrop-blur-[2px] p-4 pt-[16vh]" onClick={cancel} onKeyDown={onKey}>
      <div
        ref={boxRef}
        tabIndex={-1}
        className="w-full max-w-sm rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 shadow-2xl p-5"
        style={{ animation: 'fadeIn 160ms ease-out' }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <h2 className="text-[15px] font-semibold tracking-tight">{req.title}</h2>
        {req.body && <p className="mt-1 whitespace-pre-line text-sm text-zinc-500">{req.body}</p>}

        {req.kind === 'text' && (
          <div className="mt-3">
            {(req as TextReq).label && <label className="mb-1 block text-[11px] font-medium uppercase tracking-[0.08em] text-zinc-500">{(req as TextReq).label}</label>}
            {(req as TextReq).multiline ? (
              <Textarea ref={areaRef} rows={3} value={value} placeholder={(req as TextReq).placeholder} onChange={(e) => setValue(e.target.value)} />
            ) : (
              <Input ref={inputRef} value={value} placeholder={(req as TextReq).placeholder} onChange={(e) => setValue(e.target.value)} />
            )}
          </div>
        )}
        {req.kind === 'number' && (
          <div className="mt-3">
            {(req as NumberReq).label && <label className="mb-1 block text-[11px] font-medium uppercase tracking-[0.08em] text-zinc-500">{(req as NumberReq).label}</label>}
            <Input ref={inputRef} inputMode="numeric" value={value} placeholder={(req as NumberReq).placeholder} onChange={(e) => setValue(e.target.value)} />
            {(req as NumberReq).hint && <p className="mt-1 text-xs text-zinc-400">{(req as NumberReq).hint}</p>}
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={cancel}>{req.cancelLabel ?? 'Zrušit'}</Button>
          <Button variant={req.danger ? 'danger' : 'primary'} onClick={submit}>
            {req.confirmLabel ?? (req.kind === 'confirm' ? 'Potvrdit' : 'OK')}
          </Button>
        </div>
      </div>
    </div>
  );
}
